import React, { useState, useRef } from 'react';
import api from '../../../utils/api';

const CATEGORIES = ['Goat', 'Sheep', 'Bakra', 'Other'];
const BREEDS = ['Boer', 'Dorper', 'Merino', 'Kalahari Red', 'Angora', 'Damara', 'Saanen', 'Beetal', 'Sirohi', 'Other'];
const HEALTH_OPTIONS = [
  { value: 'healthy', label: 'Healthy — Vaccinated' },
  { value: 'good', label: 'Good Condition' },
  { value: 'needs_attention', label: 'Needs Attention' },
];

const inputClass = (hasErr) =>
                          `w-full px-3 py-2 rounded-xl border bg-surface-container-low text-sm text-on-surface outline-none focus:ring-2 transition-all placeholder:text-outline-variant ${
                            hasErr ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : 'border-outline-variant/40 focus:border-primary focus:ring-primary/10'
                          }`;

const selectClass = `w-full px-3 py-2 rounded-xl border border-outline-variant/40
                     bg-surface-container-low text-sm outline-none focus:border-primary transition-all`;

const EMPTY_ANIMAL = () => ({
  id: Date.now() + Math.random(),
  name: '',
  category: 'Goat',
  breed: '',
  price_inr: '',
  age_months: '',
  weight_kg: '',
  teeth_count: '',
  gender: 'Male',
  health_status: 'healthy',
  quantity: 1,
  images: [],
});

// ── Single Animal Row ──────────────────────────────────────
function AnimalRow({ animal, index, total, onChange, onRemove, sharedImage, errors = {} }) {
  const fileRef = useRef(null);
  const isGroup = Number(animal.quantity) > 1;

  const blockInvalidNumeric = (e) => {
    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
  };
  const blockDecimalAndInvalid = (e) => {
    if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault();
  };

  const set = (k, v) => onChange(animal.id, k, v);

  const handleFiles = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 5 - (animal.images?.length || 0);
    const toAdd = files.slice(0, remaining).map(file => ({
      file,
      preview_url: URL.createObjectURL(file),
    }));
    set('images', [...(animal.images || []), ...toAdd]);
    e.target.value = '';
  };

const removeImage = (imgIdx) => {
  set('images', animal.images.filter((_, i) => i !== imgIdx));
};

  return (
    <div className="bg-surface-container-low/60 rounded-2xl border border-outline-variant/20 overflow-hidden">
      {/* Row header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-outline-variant/10">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-primary text-white text-[11px] font-black flex items-center justify-center">
            {index + 1}
          </div>
          <span className="text-sm font-bold text-on-surface">
            {animal.name || `Animal ${index + 1}`}
          </span>
          {Number(animal.quantity) > 1 && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              Group × {animal.quantity}
            </span>
          )}
        </div>
        {total > 1 && (
          <button onClick={() => onRemove(animal.id)}
            className="w-7 h-7 rounded-full hover:bg-red-50 text-secondary hover:text-red-500
                       flex items-center justify-center transition-all">
            <span className="material-symbols-outlined text-[16px]">close</span>
          </button>
        )}
      </div>

      <div className="p-4 space-y-3">
        {/* Category + Breed */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Category</label>
            <select className={selectClass} value={animal.category} onChange={e => set('category', e.target.value)}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Breed</label>
            <select className={selectClass} value={animal.breed} onChange={e => set('breed', e.target.value)}>
              <option value="">Select breed</option>
              {BREEDS.map(b => <option key={b}>{b}</option>)}
            </select>
          </div>
        </div>

        {/* Name + Price */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">
              Animal Name *
            </label>
            <input className={inputClass(errors[`name_${index}`])} value={animal.name}
              placeholder="e.g. Titan Buck" onChange={e => set('name', e.target.value)} />
            {errors[`name_${index}`] && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors[`name_${index}`]}</p>}
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">
              Price per Animal (₹) *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary text-sm font-bold">₹</span>
              <input className={`${inputClass(errors[`price_${index}`])} pl-7`} type="number"
                value={animal.price_inr} placeholder="45000"
                onKeyDown={blockInvalidNumeric}
                onChange={e => set('price_inr', e.target.value)} />
            </div>
            {errors[`price_${index}`] && (
              <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors[`price_${index}`]}</p>
            )}
            {isGroup && animal.price_inr && Number(animal.price_inr) > 0 && (
              <div className="mt-1.5 flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                <span className="text-[10px] text-emerald-600 font-semibold">
                  ₹{Number(animal.price_inr).toLocaleString('en-IN')}
                  <span className="mx-1 text-emerald-400">×</span>
                  {animal.quantity}
                  <span className="mx-1 text-emerald-400">=</span>
                </span>
                <span className="text-[12px] text-emerald-800 font-black">
                  ₹{(Number(animal.price_inr) * Number(animal.quantity)).toLocaleString('en-IN')} total
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Age + Weight + Teeth + Quantity */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1 whitespace-nowrap">Age (mo)</label>
            <input className={inputClass(errors[`age_${index}`])} type="number" value={animal.age_months}
              placeholder="18" onKeyDown={blockInvalidNumeric} onChange={e => set('age_months', e.target.value)} />
            {errors[`age_${index}`] && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors[`age_${index}`]}</p>}
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1 whitespace-nowrap">Weight (kg)</label>
            <input className={inputClass(errors[`weight_${index}`])} type="number" value={animal.weight_kg}
              placeholder="45" onKeyDown={blockInvalidNumeric} onChange={e => set('weight_kg', e.target.value)} />
            {errors[`weight_${index}`] && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors[`weight_${index}`]}</p>}
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1 whitespace-nowrap">Teeth</label>
            <input className={inputClass(errors[`teeth_${index}`])} type="number" value={animal.teeth_count}
              placeholder="4" onKeyDown={blockDecimalAndInvalid} onChange={e => set('teeth_count', e.target.value)} />
            {errors[`teeth_${index}`] && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors[`teeth_${index}`]}</p>}
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1 whitespace-nowrap">Qty</label>
            <input className={inputClass(errors[`qty_${index}`])} type="number" min="1" value={animal.quantity}
              placeholder="1" onKeyDown={blockDecimalAndInvalid} onChange={e => set('quantity', e.target.value)} />
            {errors[`qty_${index}`] && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors[`qty_${index}`]}</p>}
          </div>
        </div>

        {/* Gender + Health */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Gender</label>
            <select className={selectClass} value={animal.gender} onChange={e => set('gender', e.target.value)}>
              <option value="Male">Male (Buck)</option>
              <option value="Female">Female (Doe)</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Health</label>
            <select className={selectClass} value={animal.health_status} onChange={e => set('health_status', e.target.value)}>
              {HEALTH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* Multi Image Upload */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">
            Animal Photos *
            <span className="ml-1 normal-case font-normal tracking-normal text-secondary">
              (min 1, max 5)
            </span>
          </label>

          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-2">
            {(animal.images || []).map((img, imgIdx) => (
              <div key={imgIdx} className="relative aspect-square">
                <img src={img.preview_url} alt={`animal ${imgIdx + 1}`}
                  className="w-full h-full object-cover rounded-xl" />
                <button type="button" onClick={() => removeImage(imgIdx)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white
                            rounded-full text-[10px] flex items-center justify-center
                            hover:bg-red-600 transition-all shadow-sm">
                  ✕
                </button>
                {imgIdx === 0 && (
                  <span className="absolute bottom-1 left-1 text-[9px] font-bold
                                  bg-black/50 text-white px-1 py-0.5 rounded-md">
                    Main
                  </span>
                )}
              </div>
            ))}

            {(animal.images?.length || 0) < 5 && (
              <div onClick={() => fileRef.current.click()}
                className="aspect-square border-2 border-dashed border-outline-variant/40
                          rounded-xl flex flex-col items-center justify-center cursor-pointer
                          hover:border-primary/40 hover:bg-primary/5 transition-all">
                <span className="material-symbols-outlined text-outline-variant text-2xl">
                  add_photo_alternate
                </span>
                <p className="text-[10px] text-secondary mt-0.5">Add</p>
              </div>
            )}
          </div>

          <p className="text-[11px] text-secondary mb-2">
            {animal.images?.length || 0}/5 photos
            {(animal.images?.length || 0) === 0 && (
              <span className="text-red-400 ml-1">— at least 1 required</span>
            )}
          </p>

          {sharedImage && (animal.images?.length || 0) === 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-xl">
              <span className="material-symbols-outlined text-blue-500 text-[16px]">info</span>
              <p className="text-[11px] text-blue-700 font-semibold">
                Shared group photo will be used. Upload here to override.
              </p>
            </div>
          )}

          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
            multiple className="hidden" onChange={handleFiles} />
        </div>

        {errors[`img_${index}`] && (
          <p className="mt-0.5 text-[10px] text-red-500 font-semibold flex items-center gap-0.5">
            <span className="material-symbols-outlined" style={{fontSize:'11px'}}>error</span>
            {errors[`img_${index}`]}
          </p>
        )}

        {sharedImage && (animal.images?.length || 0) === 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
            <img src={sharedImage} alt="shared" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
            <div>
              <p className="text-[11px] text-amber-700 font-bold">Using shared group photo</p>
              <p className="text-[10px] text-amber-600">Upload above to use a different photo</p>
            </div>
          </div>
        )}
        {sharedImage && (animal.images?.length || 0) > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-primary/5 border border-primary/20 rounded-xl">
            <span className="material-symbols-outlined text-primary text-[16px]">check_circle</span>
            <p className="text-[11px] text-primary font-bold">Using own photos — overrides shared ✓</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Panel ─────────────────────────────────────────────
export default function AddAnimalPanel({ onSubmit, onCancel }) {
  const [common, setCommon] = useState({
    category: 'Goat',
    breed: '',
    location_city: '',
    location_state: '',
    description: '',
  });

  const [animals, setAnimals] = useState([EMPTY_ANIMAL()]);
  const [sharedImageFile, setSharedImageFile] = useState(null);
  const [sharedImagePreview, setSharedImagePreview] = useState('');
  const sharedFileRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const setC = (k, v) => setCommon(f => ({ ...f, [k]: v }));

  const handleAnimalChange = (id, key, value) => {
    setAnimals(prev => prev.map(a => a.id === id ? { ...a, [key]: value } : a));
  };

  const addAnimal = () => setAnimals(prev => [...prev, EMPTY_ANIMAL()]);
  const removeAnimal = (id) => setAnimals(prev => prev.filter(a => a.id !== id));
  const hasGroups = animals.some(a => Number(a.quantity) > 1);

  const handleSharedFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSharedImageFile(file);
    setSharedImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    setErr('');

    const nameRegex   = /^[a-zA-Z0-9\s'-]{2,60}$/;
    const cityRegex   = /^[a-zA-Z\s'-]{2,50}$/;
    const positiveInt = (v) => /^\d+$/.test(String(v)) && Number(v) >= 1;
    const positiveNum = (v) => /^\d+(\.\d+)?$/.test(String(v)) && Number(v) > 0;

    const newErrors = {};
    if (!common.location_city.trim() || !cityRegex.test(common.location_city.trim()))
      newErrors.location_city = 'Enter a valid city name (letters only)';
    if (!common.location_state.trim() || !cityRegex.test(common.location_state.trim()))
      newErrors.location_state = 'Enter a valid state name (letters only)';

    animals.forEach((a, i) => {
      if (!a.name.trim() || !nameRegex.test(a.name.trim()))
        newErrors[`name_${i}`] = 'Name must be 2–60 chars, letters/numbers only';
      if (!a.price_inr || !positiveNum(a.price_inr))
        newErrors[`price_${i}`] = 'Enter a valid price (numbers only)';
      if (a.age_months !== '' && !positiveNum(a.age_months))
        newErrors[`age_${i}`] = 'Must be a positive number';
      if (a.weight_kg !== '' && !positiveNum(a.weight_kg))
        newErrors[`weight_${i}`] = 'Must be a positive number';
      if (a.teeth_count !== '' && (!positiveInt(a.teeth_count) || Number(a.teeth_count) > 32))
        newErrors[`teeth_${i}`] = 'Teeth must be 1–32';
      if (!positiveInt(a.quantity))
        newErrors[`qty_${i}`] = 'Quantity must be a whole number ≥ 1';
      if ((!a.images || a.images.length === 0) && !sharedImageFile)
        newErrors[`img_${i}`] = 'At least 1 image is required';
    });

    if (Object.keys(newErrors).length) {
      setFieldErrors(newErrors);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      for (const animal of animals) {
        const fd = new FormData();

        fd.append('category', animal.category);
        fd.append('breed', animal.breed);
        fd.append('location_city', common.location_city);
        fd.append('location_state', common.location_state);
        fd.append('description', common.description);

        fd.append('name', animal.name);
        fd.append('price_inr', animal.price_inr);
        fd.append('age_months', animal.age_months || '');
        fd.append('weight_kg', animal.weight_kg || '');
        fd.append('teeth_count', animal.teeth_count || '');
        fd.append('gender', animal.gender);
        fd.append('health_status', animal.health_status);
        fd.append('quantity', animal.quantity || 1);

        // First image as primary, fallback to shared
        if (animal.images && animal.images.length > 0) {
          fd.append('animal_image', animal.images[0].file);
        } else if (sharedImageFile) {
          fd.append('animal_image', sharedImageFile);
        }

        const result = await onSubmit(fd); // must return { id }

        // Upload extra images 2–5
        if (animal.images && animal.images.length > 1 && result?.id) {
          const extraFd = new FormData();
          animal.images.slice(1).forEach(img => {
            extraFd.append('images', img.file);
          });
          await api.post(`/api/farmer/animals/${result.id}/images`, extraFd);
        }
      }

      setSuccess(true);
      setTimeout(() => { setSuccess(false); onCancel(); }, 1800);
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Failed to add animals.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5">

      {/* ── Common Details ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">
        <div className="p-5 border-b border-surface-container-high flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
            <span className="material-symbols-outlined text-primary text-[20px]">tune</span>
          </div>
          <div>
            <h3 className="font-bold text-base">Common Details</h3>
            <p className="text-[11px] text-secondary">Shared across all animals in this submission</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">City</label>
              <input className={inputClass(fieldErrors.location_city)} value={common.location_city}
                placeholder="e.g. Hyderabad" onChange={e => setC('location_city', e.target.value)} />
              {fieldErrors.location_city && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{fieldErrors.location_city}</p>}
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">State</label>
              <input className={inputClass(fieldErrors.location_state)} value={common.location_state}
                placeholder="e.g. Telangana" onChange={e => setC('location_state', e.target.value)} />
              {fieldErrors.location_state && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{fieldErrors.location_state}</p>}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Description</label>
            <textarea className={`${inputClass(false)} resize-none`} rows={2} value={common.description}
              placeholder="Describe the animals — lineage, temperament, feed history..."
              onChange={e => setC('description', e.target.value)} />
          </div>
        </div>
      </div>

      {/* ── Shared Group Photo ── */}
      {hasGroups && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-amber-600 text-[20px]">photo_library</span>
            <div>
              <p className="text-sm font-bold text-amber-800">Shared Group Photo</p>
              <p className="text-[11px] text-amber-600">
                Used as fallback for any animal without its own photo. Individual photos always take priority.
              </p>
            </div>
          </div>
          <div onClick={() => sharedFileRef.current.click()}
            className={`border-2 border-dashed rounded-xl p-4 cursor-pointer transition-all ${
              sharedImagePreview ? 'border-amber-400 bg-white' : 'border-amber-300 hover:border-amber-400'
            }`}>
            {sharedImagePreview ? (
              <div className="flex items-center gap-3">
                <img src={sharedImagePreview} alt="shared" className="h-16 w-16 object-cover rounded-xl flex-shrink-0" />
                <div className="text-left flex-1">
                  <p className="text-xs font-bold text-amber-700">✓ Group photo set</p>
                  <p className="text-[10px] text-amber-600">Click to change</p>
                </div>
                <button onClick={e => { e.stopPropagation(); setSharedImageFile(null); setSharedImagePreview(''); }}
                  className="text-[10px] text-red-500 font-bold hover:underline flex-shrink-0">Remove</button>
              </div>
            ) : (
              <div className="flex items-center gap-2 justify-center">
                <span className="material-symbols-outlined text-2xl text-amber-400">cloud_upload</span>
                <p className="text-xs font-semibold text-amber-700">Upload shared group photo</p>
              </div>
            )}
            <input ref={sharedFileRef} type="file" accept="image/jpeg,image/png,image/webp"
              className="hidden" onChange={handleSharedFile} />
          </div>
        </div>
      )}

      {/* ── Animals List ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">
        <div className="p-5 border-b border-surface-container-high flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
              <span className="material-symbols-outlined text-emerald-600 text-[20px]">pets</span>
            </div>
            <div>
              <h3 className="font-bold text-base">Animals</h3>
              <p className="text-[11px] text-secondary">{animals.length} animal{animals.length !== 1 ? 's' : ''} to submit</p>
            </div>
          </div>
          <button onClick={addAnimal}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary/10 text-primary
                       text-xs font-bold hover:bg-primary/20 transition-all">
            <span className="material-symbols-outlined text-[16px]">add</span>
            Add Another
          </button>
        </div>

        <div className="p-4 space-y-3">
          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
              <span className="material-symbols-outlined text-base">error</span>{err}
            </div>
          )}
          {success && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
              <span className="material-symbols-outlined text-base">check_circle</span>
              {animals.length} animal{animals.length !== 1 ? 's' : ''} submitted! Redirecting…
            </div>
          )}

          {animals.map((animal, i) => (
            <AnimalRow
              key={animal.id}
              animal={animal}
              index={i}
              total={animals.length}
              onChange={handleAnimalChange}
              onRemove={removeAnimal}
              sharedImage={sharedImagePreview}
              errors={fieldErrors}  
            />
          ))}

          <button onClick={addAnimal}
            className="w-full py-3 rounded-2xl border-2 border-dashed border-outline-variant/30
                       text-secondary text-sm font-semibold hover:border-primary/30
                       hover:text-primary hover:bg-primary/5 transition-all flex items-center
                       justify-center gap-2">
            <span className="material-symbols-outlined text-[18px]">add_circle</span>
            Add Another Animal
          </button>
        </div>

        <div className="px-5 pb-5 flex gap-3 justify-end border-t border-surface-container-high pt-4">
          <button onClick={onCancel}
            className="px-5 py-2.5 rounded-full border border-outline-variant text-secondary
                       font-semibold text-sm hover:bg-surface-container transition-all">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            className="px-6 py-2.5 rounded-full bg-primary text-white font-bold text-sm
                       hover:opacity-90 transition-all disabled:opacity-60 flex items-center gap-2">
            {submitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Submitting {animals.length} animal{animals.length !== 1 ? 's' : ''}…
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[18px]">send</span>
                Submit {animals.length} Animal{animals.length !== 1 ? 's' : ''} for Review
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}